import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Loader2 } from "lucide-react";
import useVoiceToDesign, { useSpacebarPushToTalk, speak, useVoicePrefs } from "../lib/voice";

/**
 * MicButton — drop-in voice input button for any prompt area.
 *
 * Props:
 *   onTranscript(text, meta)  — called when transcription is finalised
 *   language                  — ISO 639-1 code (default 'en')
 *   prompt                    — Whisper biasing prompt (engineering vocab default)
 *   pushToTalkSpacebar        — bool, hold spacebar to record (default true)
 *   speakReply                — bool, TTS the transcript back (default false)
 *   size                      — 'sm' | 'md' | 'lg'
 *   variant                   — 'inline' | 'floating'
 *   testId                    — data-testid override
 */
export default function MicButton({
  onTranscript, onAutoSubmit,
  language = null, prompt = null,
  pushToTalkSpacebar = true, speakReply = null,
  size = "md", variant = "inline",
  testId = "mic-button",
}) {
  // Honor the user's TTS-feedback preference unless explicitly overridden.
  const prefs = useVoicePrefs();
  const ttsEnabled = speakReply ?? !!prefs.feedback;
  // Single-fire guard: a long sentence containing 'send' twice should
  // only call onAutoSubmit once per recording session.
  const submittedRef = useRef(false);
  const v = useVoiceToDesign({
    language, prompt,
    onTranscript: (text, meta) => {
      submittedRef.current = false;
      onTranscript?.(text, meta);
      if (ttsEnabled) speak(text, { lang: (language || prefs.language) === "hi" ? "hi-IN" : "en-IN" });
    },
    onAutoSubmit: (text, meta) => {
      if (submittedRef.current) return;
      submittedRef.current = true;
      onAutoSubmit?.(text, meta);
    },
  });
  const [showCorrection, setShowCorrection] = useState(false);

  useSpacebarPushToTalk({
    enabled: pushToTalkSpacebar,
    onStart: () => { if (!v.isListening && !v.isProcessing) v.start(); },
    onStop:  () => { if (v.isListening) v.stop(); },
  });

  // Show "Did you mean?" briefly when corrections happened
  useEffect(() => {
    if (v.hadCorrections && v.transcript) {
      setShowCorrection(true);
      const t = setTimeout(() => setShowCorrection(false), 4500);
      return () => clearTimeout(t);
    }
  }, [v.hadCorrections, v.transcript]);

  const px = size === "sm" ? 28 : size === "lg" ? 48 : 36;
  const baseStyle = {
    width: px, height: px, borderRadius: "50%",
    border: "1px solid var(--pf-border)",
    background: v.isListening
        ? "#EF4444"
        : v.isProcessing ? "#F59E0B" : "var(--pf-bg)",
    color: v.isListening || v.isProcessing ? "white" : "#F26B1F",
    cursor: v.isProcessing ? "wait" : "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    transition: "all .15s",
    boxShadow: v.isListening ? "0 0 0 4px rgba(239,68,68,0.25)" : "none",
    animation: v.isListening
        ? "rachana-mic-pulse 1s infinite ease-in-out" : "none",
    position: variant === "floating" ? "fixed" : "relative",
    bottom: variant === "floating" ? 24 : "auto",
    right: variant === "floating" ? 24 : "auto",
    zIndex: variant === "floating" ? 999 : "auto",
  };
  const iconSize = size === "sm" ? 14 : size === "lg" ? 22 : 18;

  return (
    <>
      <style>{`
        @keyframes rachana-mic-pulse {
          0%,100% { transform: scale(1); box-shadow: 0 0 0 4px rgba(239,68,68,0.25); }
          50%     { transform: scale(1.08); box-shadow: 0 0 0 10px rgba(239,68,68,0.0); }
        }
      `}</style>
      <button data-testid={testId}
          aria-label={v.isListening ? "Stop recording" : "Start voice input"}
          title={v.isListening ? "Stop · or release Space"
                                          : "Click or hold Space to talk · releases on stop"}
          onClick={v.toggle} style={baseStyle} disabled={v.isProcessing}>
        {v.isProcessing
            ? <Loader2 size={iconSize} className="animate-spin"
                              style={{ animation: "spin 1s linear infinite" }} />
            : v.isListening
                ? <MicOff size={iconSize} />
                : <Mic size={iconSize} />}
      </button>
      {(v.error || (showCorrection && v.transcript)) && (
        <div role="status" data-testid={`${testId}-feedback`}
            style={{
              position: "absolute", bottom: variant === "floating" ? 84 : -34,
              right: variant === "floating" ? 24 : 0,
              background: v.error ? "#7f1d1d" : "#0F131C",
              border: `1px solid ${v.error ? "#EF4444" : "#F26B1F"}`,
              color: "white", padding: "6px 10px", fontSize: 11,
              borderRadius: 4, maxWidth: 320, zIndex: 100,
              boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
            }}>
          {v.error
              ? `⚠ ${v.error}`
              : <>
                  <div style={{ fontSize: 9, color: "#F26B1F" }}>HEARD ↦ CORRECTED</div>
                  <div style={{ marginTop: 2, opacity: 0.7,
                                          fontStyle: "italic" }}>"{v.raw}"</div>
                  <div style={{ marginTop: 2 }}>"{v.transcript}"</div>
                </>}
        </div>
      )}
    </>
  );
}
